import {
  getInvitationTemplateLengthError,
  getMergeTokenValidationError,
  INVITE_MERGE_TOKEN_KEYS,
} from "./mergeTokens";
import {
  getSequenceDelayValidationError,
  normalizeSequenceDelayUnit,
  type SequenceDelayUnit,
} from "./sequenceDelay";

export const SEQUENCE_TEMPLATE_KIND = "SEQUENCE" as const;
export const LINKEDIN_MESSAGE_MAX_LENGTH = 8000;
export const EMAIL_SUBJECT_MAX_LENGTH = 200;
export const EMAIL_MESSAGE_MAX_LENGTH = 10000;
export const DEFAULT_INVITATION_MESSAGE = "Hi {{firstName}} - would love to connect";
export const DEFAULT_SEND_MESSAGE = "Hi {{firstName}}, reaching out regarding your profile.";
export const DEFAULT_EMAIL_SUBJECT = "Following up, {{firstName}}";
export const DEFAULT_EMAIL_MESSAGE =
  "Hi {{firstName}},\n\nI wanted to share a quick note about how we can help.\n\nBest regards";
export const EMAIL_SEND_WHEN_COPY =
  "Sends only if the prospect has an email address and your email channel is verified.";
export const LINKEDIN_SEND_WHEN_COPY = "Prospect can receive this LinkedIn action";
export const EMAIL_CHANNEL_REQUIRED_MESSAGE =
  "Verify your email channel in Integrations before adding an Email action.";

export type SequenceActionType =
  | "sendConnectionRequest"
  | "viewProfile"
  | "sendInMail"
  | "sendEmail";

export type SequenceStep = {
  id: string;
  actionType: SequenceActionType;
  delayDays: number;
  delayUnit: SequenceDelayUnit;
  continueIfNoReply: boolean;
  subject: string;
  message: string;
  sendWithoutNote: boolean;
};

export type SequenceTemplatePayload = {
  version: 1;
  steps: SequenceStep[];
};

export const SEQUENCE_ACTION_OPTIONS: Array<{
  actionType: SequenceActionType;
  label: string;
  description: string;
  pathLabel: string;
  repeatable: boolean;
}> = [
  {
    actionType: "sendConnectionRequest",
    label: "Send Invitation",
    description: "LinkedIn connection request",
    pathLabel: "Send Invitation",
    repeatable: false,
  },
  {
    actionType: "viewProfile",
    label: "Visit Profile",
    description: "Open the prospect's LinkedIn profile",
    pathLabel: "Visit Profile",
    repeatable: true,
  },
  {
    actionType: "sendInMail",
    label: "Send Message",
    description: "Message content is configured here",
    pathLabel: "Send Message",
    repeatable: true,
  },
  {
    actionType: "sendEmail",
    label: "Email",
    description: "Only when email + verified sending",
    pathLabel: "Email",
    repeatable: true,
  },
];

const SEQUENCE_ACTION_TYPES: SequenceActionType[] = SEQUENCE_ACTION_OPTIONS.map(
  (option) => option.actionType
);

export const isSequenceActionType = (value: unknown): value is SequenceActionType =>
  SEQUENCE_ACTION_TYPES.includes(value as SequenceActionType);

export const createSequenceStepId = () =>
  `step-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getSequenceActionMeta = (actionType: SequenceActionType) =>
  SEQUENCE_ACTION_OPTIONS.find((option) => option.actionType === actionType);

export const isSequenceTemplate = (template: any): boolean => {
  if (!template || typeof template !== "object") return false;
  if (String(template.type || "").toUpperCase() === SEQUENCE_TEMPLATE_KIND) {
    return true;
  }
  const content = template.content || {};
  return (
    content.kind === SEQUENCE_TEMPLATE_KIND ||
    content.subject === SEQUENCE_TEMPLATE_KIND
  );
};

export const getCopyableContentTemplates = (templates: any[]) =>
  (Array.isArray(templates) ? templates : []).filter(
    (template) => !isSequenceTemplate(template)
  );

export const parseSequencePayload = (
  body: unknown
): SequenceTemplatePayload | null => {
  if (!body) return null;
  try {
    const parsed = typeof body === "string" ? JSON.parse(body) : body;
    if (!parsed || typeof parsed !== "object") return null;
    const steps = Array.isArray((parsed as SequenceTemplatePayload).steps)
      ? (parsed as SequenceTemplatePayload).steps
      : Array.isArray(parsed)
        ? (parsed as SequenceStep[])
        : null;
    if (!steps) return null;
    return {
      version: 1,
      steps: steps
        .filter((step) => step && typeof step === "object")
        .map((step) => ({
          id: String(step.id || createSequenceStepId()),
          actionType: step.actionType,
          delayDays: Math.max(0, Number(step.delayDays) || 0),
          delayUnit: normalizeSequenceDelayUnit(step.delayUnit),
          continueIfNoReply: step.continueIfNoReply !== false,
          subject:
            step.actionType === "sendEmail" ? String(step.subject || "") : "",
          message: String(step.message || ""),
          sendWithoutNote:
            step.actionType === "sendConnectionRequest"
              ? Boolean(step.sendWithoutNote)
              : false,
        }))
        .filter((step): step is SequenceStep => isSequenceActionType(step.actionType)),
    };
  } catch {
    return null;
  }
};

export const serializeSequencePayload = (steps: SequenceStep[]): string =>
  JSON.stringify({ version: 1, steps } satisfies SequenceTemplatePayload);

export const getSequencePathLabel = (steps: SequenceStep[]): string => {
  const labels = steps
    .map((step) => getSequenceActionMeta(step.actionType)?.pathLabel)
    .filter(Boolean);
  return labels.length ? labels.join(" → ") : "No actions yet";
};

export const getSequenceStepsFromTemplate = (template: any): SequenceStep[] =>
  parseSequencePayload(template?.content?.body)?.steps || [];

export const stepSupportsMessage = (actionType: SequenceActionType) =>
  actionType === "sendConnectionRequest" ||
  actionType === "sendInMail" ||
  actionType === "sendEmail";

export const getSequenceStepSubjectError = (
  step: SequenceStep
): string | undefined => {
  if (step.actionType !== "sendEmail") return undefined;
  const subject = step.subject.trim();
  if (!subject) return "Email subject is required.";
  if (subject.length > EMAIL_SUBJECT_MAX_LENGTH) {
    return `Subject cannot be more than ${EMAIL_SUBJECT_MAX_LENGTH} characters`;
  }
  return getMergeTokenValidationError(subject, INVITE_MERGE_TOKEN_KEYS);
};

export const getSequenceStepMessageError = (step: SequenceStep): string | undefined => {
  if (!stepSupportsMessage(step.actionType)) return undefined;
  if (step.actionType === "sendConnectionRequest") {
    if (step.sendWithoutNote) return undefined;
    if (!step.message.trim()) return "Message text is required.";
    return (
      getMergeTokenValidationError(step.message, INVITE_MERGE_TOKEN_KEYS) ||
      getInvitationTemplateLengthError(step.message)
    );
  }
  if (step.actionType === "sendEmail") {
    if (!step.message.trim()) return "Email body is required.";
    return (
      getMergeTokenValidationError(step.message, INVITE_MERGE_TOKEN_KEYS) ||
      (step.message.length > EMAIL_MESSAGE_MAX_LENGTH
        ? `Email body cannot be more than ${EMAIL_MESSAGE_MAX_LENGTH} characters`
        : undefined)
    );
  }
  if (!step.message.trim()) return "Message text is required.";
  return (
    getMergeTokenValidationError(step.message, INVITE_MERGE_TOKEN_KEYS) ||
    (step.message.length > LINKEDIN_MESSAGE_MAX_LENGTH
      ? `Message cannot be more than ${LINKEDIN_MESSAGE_MAX_LENGTH} characters`
      : undefined)
  );
};

export const getSequenceStepDelayError = (
  step: SequenceStep,
  options: { isFirstStep?: boolean } = {}
): string | undefined => {
  if (options.isFirstStep) return undefined;
  const error = getSequenceDelayValidationError(
    String(step.delayDays),
    normalizeSequenceDelayUnit(step.delayUnit),
    { allowZero: true }
  );
  return error || undefined;
};

export const getSequenceStepValidationError = (
  step: SequenceStep,
  options: { isFirstStep?: boolean } = {}
): string | undefined =>
  getSequenceStepSubjectError(step) ||
  getSequenceStepMessageError(step) ||
  getSequenceStepDelayError(step, options);

export const getSequenceStepSubtitle = (step: SequenceStep): string => {
  const meta = getSequenceActionMeta(step.actionType);
  if (step.actionType === "sendEmail") {
    const preview = [step.subject.trim(), step.message.trim()].filter(Boolean).join(" · ");
    if (!preview) return "Only when email + verified sending";
    return preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
  }
  if (step.actionType === "sendInMail") {
    const preview = step.message.trim();
    if (!preview) return "Message content is configured here";
    return preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
  }
  if (step.actionType === "sendConnectionRequest") {
    if (step.sendWithoutNote) return "Send without note";
    const preview = step.message.trim();
    if (!preview) return "LinkedIn connection request";
    return preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
  }
  return meta?.description || "";
};
